import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { TurnTranscript, TurnType } from '@db8/shared'
import { Transcript } from './Transcript'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { useState, useMemo, useRef } from 'react'
import { useTeamColors } from '@/hooks/useTeamColors'
import { useIOSScrollFix } from '@/hooks/useIOSScrollFix'

type Props = { panelHeight?: number }

export const TranscriptsPanel: React.FC<Props> = ({ panelHeight }) => {
  const { transcriptions, deleteTranscript, updateTranscript, addTranscript, hallMetadata } =
    useHall()
  const { currentUser } = useHallUsers()
  const [editingId, setEditingId] = useState<string | null>(null)
  const { bgClass } = useTeamColors({ speakingRole: currentUser?.speakingRole })

  const allTranscripts = useMemo(() => {
    return Object.values(transcriptions)
      .flat()
      .sort((a, b) =>
        a.turnStep === b.turnStep ? a.turnOrder - b.turnOrder : a.turnStep - b.turnStep,
      )
  }, [transcriptions])

  const groupedByStep = useMemo(() => {
    return allTranscripts.reduce((acc: Record<number, TurnTranscript[]>, transcript) => {
      const step = transcript.turnStep
      if (!acc[step]) acc[step] = []
      acc[step].push(transcript)
      return acc
    }, {})
  }, [allTranscripts])

  const handleAddTranscript = (step: number) => {
    const current = hallMetadata?.turns.find((t) => t.step === step)
    if (!current || !currentUser) return
    const id = `${Date.now()}`
    const currentTranscripts = groupedByStep[step] ?? []
    const newTranscript: TurnTranscript = {
      id,
      text: '',
      turnOrder: currentTranscripts.length + 1,
      turnStep: step,
      hallUser: currentUser,
      role: currentUser.speakingRole,
    }
    addTranscript(step, newTranscript, true)
    setEditingId(id)
  }

  const scrollRef = useRef<HTMLDivElement>(null)
  useIOSScrollFix(scrollRef)

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      style={{ height: panelHeight ? `${panelHeight}px` : undefined }}
    >
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto ios-scroll noScrollbar px-5 py-4 pb-24"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {hallMetadata?.turns
          ?.filter((turn) => turn.type !== TurnType.Preperatory)
          .map((turn) => {
          const step = turn.step
          const title = turn.title
          const transcripts = groupedByStep[step] || []
          const isSpeaker = [turn.primarySpeakerId, turn.secondarySpeakerId].includes(
            currentUser?.id,
          )

          return (
            <div key={`turn-${step}`} className="mb-5 flex flex-col justify-center min-h-[100px]">
              <h3 className="text-md font-semibold mb-3">{title}</h3>

              {transcripts.length > 0 ? (
                transcripts.map((transcript) => (
                  <Transcript
                    key={transcript.id}
                    transcript={transcript}
                    onDelete={deleteTranscript}
                    onEdit={updateTranscript}
                    autoEdit={editingId === transcript.id}
                    setEditingId={setEditingId}
                  />
                ))
              ) : !isSpeaker ? (
                <p className="text-center text-sm text-gray-500 italic pt-4">
                  No transcripts available.
                </p>
              ) : null}

              {isSpeaker && (
                <div className="flex justify-center pt-3 w-full">
                  <Button
                    onClick={() => handleAddTranscript(step)}
                    variant="marketing"
                    className={`flex w-full max-w-none items-center gap-2 ${bgClass}`}
                  >
                    <Plus size={16} /> Add Transcript
                  </Button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
